import React, { useEffect, useMemo, useRef, useState } from "react"; 
import { useTheme } from "../theme";
import { motion } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import { Sparklines, SparklinesLine, SparklinesSpots } from "react-sparklines";
import QuickActions from "../components/quick-actions";
import Upcoming_appointments from "../components/upcoming_appointments";
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { isReminderRead } from '../lib/notificationReadState';
import useMetrics from "../hooks/useMetrics";
import { deriveQureoScore } from "../lib/qureoScore";
import { onHealthDataUpdated } from "../lib/healthSync";
import BottomNavigation from "../components/bottom_navigation";
import HealthSnapshot from "../components/snapshot"
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import Human from "../assets/icons/done.png"
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import angryImage from "../assets/icons/angry.png";
import disappointedImage from "../assets/icons/disappointed.png";
import neutralImage from "../assets/icons/netural.png";
import smilingImage from "../assets/icons/smiling.png";
import satisfiedImage from "../assets/icons/satisfied.png";
import ScienceIcon from '@mui/icons-material/Science';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WalletIcon from '@mui/icons-material/Wallet';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MinorCrashIcon from '@mui/icons-material/MinorCrash';
import {
  Bell,
  User2,
  Stethoscope,
  Clipboard,
  Box,
  CreditCard,
  Home as HomeIcon,
  Stars,
  LogIn,
  BookOpen,
  FlaskConical,
  Mic,
  MessageSquare,
 Emergency,
 User,
  Tv,
  Heart,
  Truck,
  CalendarCheck,
  Tablets,
  AlertCircle,
  LeafIcon,
  Leaf,
  HeartPulse,
  TestTubeDiagonal,
  Pill,
  Search,
  Lightbulb,
  ScanHeart,
  HeartHandshake,
  Megaphone,
  MapPin,
  FileText,
  Calendar,
  Brain,
  Settings,
  Star,
  Notebook,
  Book,
  Car,
  ChevronRight,
  Plus,
  PillIcon,
  ShieldCheckIcon,
} from "lucide-react";
import Logo from "../assets/logo.png";
import DoctorConsultBanner from "../assets/doctor-1.png";
import Banner4 from "../assets/banner/4.png";
import Banner5 from "../assets/banner/5.png";
import Banner6 from "../assets/banner/6.png";
import Banner7 from "../assets/banner/7.png";
import Banner8 from "../assets/banner/8.png";
import Banner9 from "../assets/banner/9.png";
import Banner11 from "../assets/banner/11.png";
import Banner12 from "../assets/banner/12.png";
import Banner13 from "../assets/banner/13.png";
import Banner14 from "../assets/banner/14.png";
import EhrRecord from "../assets/icons/EHR RECORD.png";
import { useNavigate } from "react-router-dom";
import HomePharmacyPreview from "../components/home_pharmacy_preview";
import HomeNearbyClinics from "../components/home_nearby_clinics";
import SearchBox from "../components/epharmacy_searchbox";

const emojis = [
  "😵",
  "😞",
  "😐",
  "😊",
  "😁",
];
const ratingImages = [
  { src: angryImage, label: "Angry" },
  { src: disappointedImage, label: "Disappointed" },
  { src: neutralImage, label: "Neutral" },
  { src: smilingImage, label: "Smiling" },
  { src: satisfiedImage, label: "Satisfied" },
];

const unwrapVitalValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" || typeof value === "string") return value;

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return unwrapVitalValue(value[value.length - 1]);
  }

  if (typeof value === "object") {
    if (value.raw != null && value.raw !== "") return value.raw;
    if (value.systolic != null && value.diastolic != null) return `${value.systolic}/${value.diastolic}`;
    if (value.value != null) {
      const numericValue = Number(value.value);
      return Number.isFinite(numericValue) ? numericValue : String(value.value);
    }
    if (Array.isArray(value.values) && value.values.length > 0) {
      return unwrapVitalValue(value.values[value.values.length - 1]);
    }
  }

  return null;
};

const toVitalNumber = (value) => {
  const unwrapped = unwrapVitalValue(value);
  if (unwrapped === null || unwrapped === undefined || unwrapped === "") return null;

  if (typeof unwrapped === "number") return Number.isFinite(unwrapped) ? unwrapped : null;
  if (typeof unwrapped === "string") {
    const numeric = Number(unwrapped);
    return Number.isFinite(numeric) ? numeric : null;
  }

  return null;
};

const getSymptomRiskMeta = (triage = "") => {
  const value = String(triage || "").toLowerCase();

  if (/emergency|urgent|immediate|high/.test(value)) {
    return {
      label: "High Risk",
      color: "#ef4444",
      bg: "#fff1f2",
      bar: "#ff4d6d",
      followUp: "Urgent review",
    };
  }

  if (/same day|24|moderate|soon/.test(value)) {
    return {
      label: "Moderate Risk",
      color: "#f97316",
      bg: "#fff7ed",
      bar: "#fb923c",
      followUp: "Check today",
    };
  }

  return {
    label: "Mild Risk",
    color: "#f59e0b",
    bg: "#fff8eb",
    bar: "#f59e0b",
    followUp: "Self care",
  };
};

const buildSymptomConditionEntries = (conditionClusters = {}) => {
  const groups = [
    { key: "mostConsistent", summary: "Primary fit", progress: 84, accent: "#ff4d6d", bg: "#fff1f2" },
    { key: "alsoPossible", summary: "Also possible", progress: 64, accent: "#f59e0b", bg: "#fff8eb" },
    { key: "lessLikely", summary: "Rule out", progress: 46, accent: "#8b5cf6", bg: "#f5f3ff" },
  ];

  return groups.flatMap(({ key, summary, progress, accent, bg }) => {
    const values = Array.isArray(conditionClusters?.[key]) ? conditionClusters[key] : [];
    return values.map((name, index) => ({
      name,
      summary,
      progress: Math.max(progress - index * 8, 28),
      accent,
      bg,
    }));
  });
};

const getSymptomSummaryFromStorage = () => {
  const sources = [];

  try {
    const currentChat = localStorage.getItem("currentChat");
    if (currentChat) {
      sources.push({
        messages: JSON.parse(currentChat),
        timestamp: localStorage.getItem("lastSaveTime"),
      });
    }
  } catch {
    // ignore invalid local symptom-chat cache
  }

  try {
    const savedChats = JSON.parse(localStorage.getItem("savedChats") || "[]");
    if (Array.isArray(savedChats)) {
      savedChats.forEach((chat) => {
        sources.push({ messages: chat?.messages, timestamp: chat?.timestamp || null });
      });
    }
  } catch {
    // ignore invalid saved symptom-chat history
  }

  const sortedSources = sources
    .filter((source) => Array.isArray(source?.messages) && source.messages.length > 0)
    .sort((a, b) => new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime());

  for (const source of sortedSources) {
    const messages = Array.isArray(source.messages) ? source.messages : [];
    const latestAssessment = [...messages].reverse().find(
      (message) => message?.role === "assistant" && (message?.conditionClusters || message?.triage || message?.actionPlan)
    );

    if (!latestAssessment) {
      continue;
    }

    const allConditions = buildSymptomConditionEntries(latestAssessment.conditionClusters);
    const seen = new Set();
    const uniqueConditions = [];

    allConditions.forEach((item) => {
      const key = String(item?.name || "").trim().toLowerCase();
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      uniqueConditions.push(item);
    });

    const riskMeta = getSymptomRiskMeta(latestAssessment?.triage);
    const careStepCount = ["immediate", "sameDay", "thisWeek", "monitor"].reduce((total, key) => {
      const values = latestAssessment?.actionPlan?.[key];
      return total + (Array.isArray(values) ? values.length : 0);
    }, 0);

    return {
      totalConditions: uniqueConditions.length,
      timestamp: source.timestamp,
      risk: riskMeta,
      careStepCount,
      items: uniqueConditions.slice(0, 2),
    };
  }

  return {
    totalConditions: 0,
    timestamp: null,
    risk: getSymptomRiskMeta(),
    careStepCount: 0,
    items: [],
  };
};

function HomeDoctorCard({ doctor, navigate }) {
  const ratingValue = Number(doctor?.rating || 5);

  const styles = {
    card: {
      width: 248,
      minWidth: 248,
      maxWidth: 248,
      minHeight: 314,
      padding: "18px 14px",
      backgroundColor: "#f5f5f6",
      borderRadius: 24,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 14,
      boxShadow: "inset 0 0 0 1px #ececef",
      lineHeight: 1.3,
      border: "1px solid #ededf0",
      fontFamily: "Inter, system-ui, sans-serif",
      boxSizing: "border-box",
      flexShrink: 0,
      scrollSnapAlign: "start",
      cursor: "pointer",
    },
    imageWrapper: {
      width: 106,
      height: 106,
      flexShrink: 0,
      borderRadius: "50%",
      overflow: "hidden",
      background: "#e6e7ec",
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "50%",
    },
    name: {
      fontSize: 20,
      fontWeight: 700,
      color: "#121217",
      lineHeight: 1.18,
      width: "100%",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      overflowWrap: "anywhere",
      wordBreak: "break-word",
      textAlign: "center",
      minHeight: 48,
      maxWidth: 210,
    },
    specialty: {
      fontSize: 16,
      color: "#6b6f7a",
      marginTop: -8,
      textAlign: "center",
      minHeight: 22,
      maxWidth: 210,
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    ratingPill: {
      marginTop: 8,
      minWidth: 92,
      height: 40,
      borderRadius: 999,
      background: "linear-gradient(180deg, #8f2bff 0%, #6f00ff 100%)",
      color: "#fff",
      border: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "0 18px",
      fontSize: 18,
      fontWeight: 700,
      letterSpacing: "0.01em",
    },
  };

  return (
    <div style={styles.card} onClick={() => navigate(`/doctor-details/${doctor._id}`)}>
      <div style={styles.imageWrapper}>
        <img
          src={doctor.avatar || doctor.image || "https://randomuser.me/api/portraits/men/7.jpg"}
          alt={doctor.name || doctor.fullName || "Doctor"}
          style={styles.image}
        />
      </div>

      <div style={styles.name}>{doctor.name || doctor.fullName || "Dr. Specialist"}</div>
      <div style={styles.specialty}>{doctor.specialty || "General Physician"}</div>

      <div style={styles.ratingPill}>
        <span>{Number.isFinite(ratingValue) ? ratingValue.toFixed(1).replace(/\.0$/, "") : "5"}</span>
        <Star size={16} color="#ffffff" fill="#ffffff" />
      </div>
    </div>
  );
}

function LabSearchRow({ onOpenSearch, searchQuery, setSearchQuery }) {
  const navigate = useNavigate();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: "6px 10px",
          background: "#f7f4ff",
          border: "1px solid #ece5ff",
          boxShadow: "0 2px 8px rgba(124,77,255,0.08)",
        }}
      >
        <div
          onClick={() => navigate("/asha-cover")}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#efe8ff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <AutoAwesomeIcon style={{ fontSize: 20, color: "var(--primary)" }} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onOpenSearch();
          }}
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
            background: "#fff",
            border: "1px solid #eee8ff",
            borderRadius: 26,
            padding: "0 46px 0 12px",
            height: 50,
            margin: 0,
          }}
        >
          <Search size={17} color="var(--primary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search through qureo"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={onOpenSearch}
            onClick={onOpenSearch}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 10,
              color: "#8a8a95",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              height: 22,
              paddingLeft: 10,
              borderLeft: "1px solid #e6e6e8",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Mic
              size={19}
              color="var(--primary)"
              style={{ cursor: "pointer" }}
              onClick={onOpenSearch}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

const Home = ({ onNavigate }) => {
  const { theme } = useTheme();
  const { user, profile, assessments } = useAuth();
  const { fetchHealthSummary, getCachedHealthSummary } = useMetrics();
  const [selected, setSelected] = useState();
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;

    setIsListening(true);
    setTranscript("");

    recognition.onresult = (event) => {
      const liveTranscript = Array.from(event.results)
        .map((result) => result[0]?.transcript || "")
        .join(" ");
      setTranscript(liveTranscript);
    };

    recognition.onend = () => {
      setTimeout(() => {
        setIsListening(false);
      }, 1200);
    };

    recognition.start();
  };

  const [snapshot] = useState([
    { id: 1, icon: "blood", label: "Blood Pressure", values: [120, 122, 118, 121], note: "Normal", color: "#22c55e" },
    { id: 2, icon: "heart", label: "Heart Rate", values: [75, 78, 72, 74], note: "Stable", color: "#3b82f6" },
    { id: 3, icon: "sugar", label: "Blood Sugar", values: [95, 100, 92, 98], note: "Good fasting", color: "#f97316" },
  ]);

const mock_thumbnail = "https://th.bing.com/th/id/R.25a0ed47ae283247bbc6b505ae6d8325?rik=%2bSt%2fqCM%2fCEzZ%2bg&pid=ImgRaw&r=0";

  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsError, setBlogsError] = useState(null);
  const [aiAssistantPreview, setAiAssistantPreview] = useState({
    text: "Start a chat with AI to get personalized health tips.",
    time: null,
    sender: "asha",
  });
  const [symptomSummary, setSymptomSummary] = useState(() => getSymptomSummaryFromStorage());
  const [goalTips, setGoalTips] = useState([]);
  const [notificationBadgeCount, setNotificationBadgeCount] = useState(0);
  const [notificationTotalCount, setNotificationTotalCount] = useState(0);
  const [healthTipBadgeCount, setHealthTipBadgeCount] = useState(0);
  const [healthTipTotalCount, setHealthTipTotalCount] = useState(0);
  const [healthSummary, setHealthSummary] = useState(null);
const searchSuggestions = [
  "Refill my meds",
  "Check Symptom",
  "Contact doctor",
  "Emergency",
  "Lab test centers",
  "E-pharmacy",
 
];
  const [tiles] = useState([
     { id: "symptom-check", title: "Checker", icon: <i class="fi fi-tr-notes-medical"></i>, color: "#ef9144", route: "symptom-checker", subtitle : "Check your symptoms" },
    { id: "consult", title: "Doctor", icon: <i class="fi fi-tr-introduction"></i>, color: "#22c55e", route : "doctor-consult", subtitle : "Live chat or video" },
      { id: "orders", title: "Shop", icon: <i class="fi fi-ts-marketplace-store"></i>, color: "#a855f7", route: "epharmacy", subtitle : "Medicine, cosmetics & more" },
    { id: "labs", title: "Lab", icon: <i class="fi fi-ts-blood-test-tube-alt"></i>, color: "#3b82f6", route: "lab-tests", subtitle : "Book Test, view reports" },
    { id: "wallet", title: "Wallet", icon: <i class="fi fi-tr-wallet-buyer"></i>, color: "#ef4444", route: "health-wallet", subtitle : "Save, Pay & Insure" },
    { id: "monitor", title: "Monitor", icon: <i class="fi fi-tr-pulse"></i>, color: "#22c55e", route: "remote",  subtitle : "Chronic care tracking" },
   { id: "records", title: "Records", icon: <i class="fi fi-ts-features"></i>,  color: "#a855f7", route: "my-health-records", subtitle : "Secure Health files" },
    { id: "learn", title: "Learn", icon: <i class="fi fi-tr-e-learning"></i>, color: "#22c55e", route: "blogs",  subtitle : "Easy health education" },
     { id: "emergency", title: "Abulance", icon: <i class="fi fi-tr-light-emergency-on"></i>, color: "#ef4444", route: "emergency-response", subtitle : "Quick emergency response" },
   
  ]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const carouselRef = useRef(null);
  const bannerRef = useRef(null);
  const [activeBanner, setActiveBanner] = useState(0);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Hello";
    if (h < 18) return "Hello";
    return "Hello";
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-scroll banner carousel every 4 seconds
  useEffect(() => {
    const TOTAL = 6;
    const timer = setInterval(() => {
      setActiveBanner((prev) => {
        const next = (prev + 1) % TOTAL;
        if (bannerRef.current) {
          bannerRef.current.scrollTo({ left: next * bannerRef.current.offsetWidth, behavior: "smooth" });
        }
        return next;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, []);

const [currentPath, setCurrentPath] = useState("/home");
const [isPopupOpen, setIsPopupOpen] = useState(false);
const navigateTo = (route) => {
  setCurrentPath(route);   // update active tab
  onNavigate && onNavigate(route);
};

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const offset = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === "right" ? offset : -offset, behavior: "smooth" });
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => scrollCarousel("right"),
    onSwipedRight: () => scrollCarousel("left"),
    trackMouse: true,
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setBlogsLoading(true);
      try {
        const res = await api.get('/api/blogs', { params: { page: 1, limit: 8 } });
        if (!mounted) return;
        // new backend returns { blogs, meta }
        if (res?.data?.blogs && Array.isArray(res.data.blogs)) {
          setBlogs(res.data.blogs);
        } else if (Array.isArray(res.data)) {
          setBlogs(res.data);
        } else {
          setBlogs([]);
        }
        setBlogsError(null);
      } catch (err) {
        console.error('Failed to load blogs from primary API', err);
        if (!mounted) return;
        setBlogs([]);
        setBlogsError('Unable to load articles right now.');
      } finally {
        if (mounted) setBlogsLoading(false);
      }
    };
    load();
    return () => { mounted = false };
  }, []);

  useEffect(() => {
    let mounted = true;

    const cached = getCachedHealthSummary();
    if (cached) {
      setHealthSummary(cached);
    }

    const refreshHealthSummary = async () => {
      try {
        const summary = await fetchHealthSummary({ forceRefresh: true });
        if (!mounted) return;
        setHealthSummary(summary || null);
      } catch {
        if (!mounted) return;
        const fallback = getCachedHealthSummary();
        setHealthSummary(fallback || null);
      }
    };

    refreshHealthSummary();
    window.addEventListener("focus", refreshHealthSummary);
    const unsubscribeHealthSync = onHealthDataUpdated(refreshHealthSummary, { debounceMs: 250 });

    return () => {
      mounted = false;
      window.removeEventListener("focus", refreshHealthSummary);
      unsubscribeHealthSync();
    };
  }, [fetchHealthSummary, getCachedHealthSummary]);

  useEffect(() => {
    let mounted = true;
    const NOTIFICATION_BASELINE_KEY = 'qureo.notificationBadgeBaseline';

    const getBaseline = () => {
      const value = Number(localStorage.getItem(NOTIFICATION_BASELINE_KEY) || 0);
      return Number.isFinite(value) ? value : 0;
    };

    const setBaseline = (value) => {
      localStorage.setItem(NOTIFICATION_BASELINE_KEY, String(Math.max(0, Number(value) || 0)));
    };

    const loadNotificationBadge = async () => {
      try {
        const [alertsRes, remindersRes] = await Promise.all([
          api.get('/api/health-alerts/unread/count').catch(() => ({ data: { unreadCount: 0 } })),
          api.get('/api/reminders').catch(() => ({ data: { reminders: [] } })),
        ]);

        console.log(remindersRes, "list of reminders")

        if (!mounted) return;

        const unreadAlerts = Number(alertsRes?.data?.unreadCount || 0);
        const reminders = Array.isArray(remindersRes?.data?.reminders) ? remindersRes.data.reminders : [];
        const dueMedicationReminders = reminders.filter((reminder) => {
          return reminder.type !== 'alert' && !isReminderRead(reminder.id);
        }).length;
        const total = Math.max(0, unreadAlerts + dueMedicationReminders);
        const baseline = getBaseline();

        // If total dropped (items read/dismissed), keep baseline in sync to avoid stale counts.
        if (total < baseline) {
          setBaseline(total);
        }

        const effectiveBaseline = total < baseline ? total : baseline;
        setNotificationTotalCount(total);
        setNotificationBadgeCount(Math.max(0, total - effectiveBaseline));
      } catch {
        if (!mounted) return;
        setNotificationTotalCount(0);
        setNotificationBadgeCount(0);
      }
    };

    loadNotificationBadge();
    const interval = setInterval(loadNotificationBadge, 30000);
    window.addEventListener('focus', loadNotificationBadge);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', loadNotificationBadge);
    };
  }, []);

  const handleOpenNotifications = () => {
    localStorage.setItem('qureo.notificationBadgeBaseline', String(notificationTotalCount));
    setNotificationBadgeCount(0);
    navigate('/notification');
  };

  const formattedNotificationBadge = notificationBadgeCount > 99 ? '99+' : String(notificationBadgeCount);

  useEffect(() => {
    let mounted = true;
    const HEALTH_TIP_BASELINE_KEY = 'qureo.healthTipBadgeBaseline';

    const getHealthTipBaseline = () => {
      const value = Number(localStorage.getItem(HEALTH_TIP_BASELINE_KEY) || 0);
      return Number.isFinite(value) ? value : 0;
    };

    const setHealthTipBaseline = (value) => {
      localStorage.setItem(HEALTH_TIP_BASELINE_KEY, String(Math.max(0, Number(value) || 0)));
    };

    const getHealthTipReminderId = (tip, index) => {
      const rawId = tip?._id || tip?.id || tip?.tipDate || `idx-${index}`;
      return `health-tip-${rawId}`;
    };

    const loadHealthTipBadge = async () => {
      try {
        const res = await api.get('/api/health-tips/recent?limit=31').catch(() => ({ data: { tips: [] } }));
        if (!mounted) return;

        const tips = Array.isArray(res?.data?.tips) ? res.data.tips : [];
        const unreadTips = tips.filter((tip, index) => !isReminderRead(getHealthTipReminderId(tip, index))).length;
        const total = Math.max(0, unreadTips);
        const baseline = getHealthTipBaseline();

        // Keep baseline in sync when total drops after user reads tips.
        if (total < baseline) {
          setHealthTipBaseline(total);
        }

        const effectiveBaseline = total < baseline ? total : baseline;
        setHealthTipTotalCount(total);
        setHealthTipBadgeCount(Math.max(0, total - effectiveBaseline));
      } catch {
        if (!mounted) return;
        setHealthTipTotalCount(0);
        setHealthTipBadgeCount(0);
      }
    };

    loadHealthTipBadge();
    const interval = setInterval(loadHealthTipBadge, 30000);
    window.addEventListener('focus', loadHealthTipBadge);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', loadHealthTipBadge);
    };
  }, []);

  const handleOpenHealthTips = () => {
    localStorage.setItem('qureo.healthTipBadgeBaseline', String(healthTipTotalCount));
    setHealthTipBadgeCount(0);
    navigate('/health-tips');
  };

  const formattedHealthTipBadge = healthTipBadgeCount > 99 ? '99+' : String(healthTipBadgeCount);

  useEffect(() => {
    let mounted = true;

    const syncSymptomSummary = () => {
      const localSummary = getSymptomSummaryFromStorage();
      if (mounted) {
        setSymptomSummary(localSummary);
      }
    };

    syncSymptomSummary();
    window.addEventListener("focus", syncSymptomSummary);

    return () => {
      mounted = false;
      window.removeEventListener("focus", syncSymptomSummary);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadAIPreview = async () => {
      try {
        const local = localStorage.getItem("qureo_ai_last_message");
        if (local) {
          const parsed = JSON.parse(local);
          if (parsed?.text && mounted) {
            setAiAssistantPreview({
              text: parsed.text,
              time: parsed.time || null,
              sender: parsed.sender || "asha",
            });
            return;
          }
        }

        const res = await api.get('/api/activities/activities');
        if (!mounted || !Array.isArray(res?.data)) return;
        const latestAIActivity = res.data.find((item) => /asha|assistant|ai chat|chatted with ai/i.test(item?.message || ""));

        if (latestAIActivity?.message) {
          setAiAssistantPreview({
            text: latestAIActivity.message,
            time: latestAIActivity.createdAt || null,
            sender: "asha",
          });
        }
      } catch {
        if (!mounted) return;
      }
    };

    loadAIPreview();
    window.addEventListener("focus", loadAIPreview);
    return () => {
      mounted = false;
      window.removeEventListener("focus", loadAIPreview);
    };
  }, []);

  const aiPreviewTimeLabel = aiAssistantPreview?.time
    ? new Date(aiAssistantPreview.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "Now";
  const displayName = profile?.fullName?.trim()?.split(/\s+/)?.[0] || user?.name || "Guest";
  const currentHour = new Date().getHours();
  const timeGreeting = currentHour < 12 ? "Good morning" : currentHour < 18 ? "Good afternoon" : "Good evening";
  const latestAssessment = Array.isArray(assessments) && assessments.length > 0
    ? [...assessments].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0]
    : (healthSummary?.latestAssessment || null);
  const summaryVitals = healthSummary?.vitals || null;
  const normalizedSummaryVitals = useMemo(() => {
    if (!summaryVitals) return null;

    return {
      bloodPressure: unwrapVitalValue(summaryVitals.bloodPressure),
      heartRate: toVitalNumber(summaryVitals.heartRate),
      oxygenLevel: toVitalNumber(summaryVitals.oxygenLevel),
      temperature: toVitalNumber(summaryVitals.temperature),
    };
  }, [summaryVitals]);
  const safeQureoScore = deriveQureoScore({
    summaryScore: healthSummary?.healthScore?.total,
    assessmentScore: latestAssessment?.score,
    bloodPressure: normalizedSummaryVitals?.bloodPressure,
    heartRate: normalizedSummaryVitals?.heartRate,
    oxygenLevel: normalizedSummaryVitals?.oxygenLevel,
    temperature: normalizedSummaryVitals?.temperature,
  }) ?? 0;
  const qureoStatusMeta = useMemo(() => {
    if (safeQureoScore >= 55) {
      return {
        label: "Healthy",
        pillBg: "#e8f7ea",
        pillColor: "#1a9f4b",
        message: "You're doing great! Keep up with healthy habits.",
      };
    }

    if (safeQureoScore >= 40) {
      return {
        label: "Watch",
        pillBg: "#fff4e5",
        pillColor: "#b45309",
        message: "A few small changes can improve your score this week.",
      };
    }

    return {
      label: "Needs Care",
      pillBg: "#feecec",
      pillColor: "#b91c1c",
      message: "Focus on daily basics and check in with your care plan.",
    };
  }, [safeQureoScore]);
  const symptomSummaryTimeLabel = symptomSummary?.timestamp
    ? new Date(symptomSummary.timestamp).toLocaleDateString([], { month: "short", day: "numeric" })
    : "Quick triage";

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nearbyDoctors, setNearbyDoctors] = useState([]);
  const [loadingNearbyHome, setLoadingNearbyHome] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchDoctors = async () => {
      try {
        setLoadingNearbyHome(true);
        const res = await api.get("/api/doctor/nearby?lat=0&lng=0&radius=50");
        if (mounted) setNearbyDoctors(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (mounted) setNearbyDoctors([]);
      } finally {
        if (mounted) setLoadingNearbyHome(false);
      }
    };
    fetchDoctors();
    return () => { mounted = false; };
  }, []);

  const buildTipsFromGoal = (goal) => {
    const primary = goal?.primaryGoal || 'Improve Sleep';
    const weekly = Number(goal?.weeklyTarget || 5);
    const reminder = goal?.reminderTime || '08:00';

    const byGoal = {
      'Improve Sleep': [
        `Sleep goal: aim for ${weekly} days this week. Start winding down 1 hour before bed.`,
        `Set your evening reminder at ${reminder} and avoid screens 30 minutes before sleep.`,
      ],
      'Lose Weight': [
        `Weight goal: stay active ${weekly} days this week with 20-30 minutes of movement.`,
        `Meal reminder set for ${reminder}: prioritize protein, vegetables, and hydration.`,
      ],
      'Build Muscle': [
        `Muscle goal: complete ${weekly} strength sessions this week with progressive overload.`,
        `Recovery reminder at ${reminder}: protein intake and sleep quality are key for growth.`,
      ],
      'Reduce Stress': [
        `Stress goal: practice breathing or mindfulness ${weekly} times this week.`,
        `Daily reminder at ${reminder}: take a 5-minute reset break to reduce stress.`,
      ],
      'Increase Energy': [
        `Energy goal: maintain ${weekly} active days and consistent hydration this week.`,
        `Reminder at ${reminder}: short walk + water can quickly improve daytime energy.`,
      ],
    };

    return byGoal[primary] || byGoal['Improve Sleep'];
  };

  useEffect(() => {
    let mounted = true;
    const loadGoalsForTips = async () => {
      try {
        const res = await api.get('/api/health-goals');
        if (!mounted) return;
        const tips = buildTipsFromGoal(res.data?.goal || null);
        setGoalTips(tips);
      } catch (err) {
        if (!mounted) return;
        setGoalTips(buildTipsFromGoal(null));
      }
    };

    loadGoalsForTips();
    return () => {
      mounted = false;
    };
  }, []);



  //"linear-gradient(100deg, #cfc2ff1a, #a9d4ff70, #ffb6d986, #ffdfaee1)"
  return (



    <div
      className="min-vh-100"
      style={{
        background: "var(--bg)",
        color: "var(--on-bg)",
        transition: "background-color 220ms ease, color 220ms ease",
        paddingBottom: 90,
        overflowX: "hidden",
      }}
    >
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .glass {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .pulse {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
          100% { opacity: 0.8; transform: scale(1); }
        }
      `}</style>

{searchOpen && (
  <>
    {/* Backdrop */}
    <div
      onClick={() => setSearchOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.25)",
        zIndex: 998,
      }}
    />

    {/* Search Bar */}
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        padding: "14px 16px",
        background: "var(--bg)",
        zIndex: 999,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        transform: "translateY(0)",
        animation: "slideDown 0.25s ease-out",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        {/* Input */}
        <SearchBox  holder={"Search through qureo"} url={"search"}/>
        {/* Close */}
        <button
          onClick={() => setSearchOpen(false)}
          style={{
            border: "none",
            background: "transparent",
            fontSize: 14,
            color: "var(--accent)",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
      </div>
    </div>

    {/* Animation */}
    <style>
      {`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}
    </style>
  </>
)}

{/* Hero Section */}
<div
  style={{
    background: "inherit",
    color: "#0f0f12",
    padding: "12px 16px",
    lineHeight: "1.3",
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  }}
>
<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 18,
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
      flex: 1,
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 3px 12px rgba(15,15,18,0.08)",
        flexShrink: 0,
      }}
    >
      <img
        src={profile?.avatar || "/avatar.png"}
        alt="Profile"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </div>

    <div style={{ minWidth: 0, flex: 1 }}>
      <div
        style={{
          fontSize: "clamp(14px, 4vw, 16px)",
          fontWeight: 600,
          color: "#4b5563",
          lineHeight: 1.1,
          whiteSpace: "nowrap",
        }}
      >
        Hello,
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
          flexWrap: "nowrap",
          marginTop: 2,
        }}
      >
        <div
          style={{
            fontSize: "clamp(20px, 5.8vw, 24px)",
            fontWeight: 700,
            color: "#050507",
            letterSpacing: "-0.03em",
            lineHeight: 1.08,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {displayName}
        </div>
        <motion.span
          animate={{ rotate: [0, 18, -8, 18, 0], y: [0, -1, 0] }}
          transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2.2 }}
          style={{ fontSize: "clamp(18px, 5vw, 22px)", lineHeight: 1, flexShrink: 0 }}
        >
          👋
        </motion.span>
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: "clamp(12px, 3.8vw, 14px)",
          color: "#6b7280",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        How can we help you today?
      </div>
    </div>
  </div>

  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
    <div
      onClick={handleOpenHealthTips}
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "1px solid #e9d5ff",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        position: "relative",
        color: "#7f00ff",
      }}
    >
      <Lightbulb size={18} style={{ color: "#7f00ff" }} />
      {healthTipBadgeCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            minWidth: 18,
            height: 18,
            padding: "0 4px",
            borderRadius: 999,
            background: "#ef4444",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {formattedHealthTipBadge}
        </span>
      )}
    </div>

    <div
      onClick={handleOpenNotifications}
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "1px solid #e9d5ff",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        position: "relative",
        color: "#7f00ff",
      }}
    >
      <NotificationsNoneIcon style={{ color: "#7f00ff", fontSize: 18 }} />
      {notificationBadgeCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            minWidth: 18,
            height: 18,
            padding: "0 4px",
            borderRadius: 999,
            background: "#ef4444",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {formattedNotificationBadge}
        </span>
      )}
    </div>
  </div>
</div>

<div
  onClick={() => navigate("/health-metrics")}
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 14,
    cursor: "pointer",
    background: "#ffffff",
    marginTop: "30px",
    border: "1px solid #eceff5",
    borderRadius: 24,
    padding: "12px 12px 12px 10px",
    boxShadow: "none",
  }}
>
  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
    <div
      style={{
        width: "clamp(86px, 23vw, 98px)",
        height: "clamp(86px, 23vw, 98px)",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 120 120" width="100%" height="100%" aria-label="Qureo score ring">
        <circle cx="60" cy="60" r="47" fill="none" stroke="#e8e0ff" strokeWidth="9" />
        <circle
          cx="60"
          cy="60"
          r="47"
          fill="none"
          stroke="#5e1ee8"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${(Math.max(0, Math.min(100, safeQureoScore)) / 100) * (Math.PI * 2 * 47)} ${Math.PI * 2 * 47}`}
          transform="rotate(-90 60 60)"
        />
      </svg>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "clamp(22px, 8.6vw, 26px)",
          fontWeight: 700,
          lineHeight: 1,
          color: "#4520cc",
          letterSpacing: "-0.03em",
        }}
      >
        {safeQureoScore}
      </div>
    </div>

    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "nowrap" }}>
        <div style={{ fontSize: "clamp(14px, 4.6vw, 18px)", fontWeight: 700, color: "#111827", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
          Qureo Score
        </div>

        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            height: 28,
            padding: "0 12px",
            borderRadius: 999,
            background: qureoStatusMeta.pillBg,
            color: qureoStatusMeta.pillColor,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {qureoStatusMeta.label}
        </span>
      </div>

      <div
        style={{
          marginTop: 8,
          color: "#4b5563",
          fontSize: "clamp(12px, 3.8vw, 15px)",
          lineHeight: 1.28,
          maxWidth: 280,
        }}
      >
        {qureoStatusMeta.message}
      </div>
    </div>
  </div>

  <ChevronRight size={30} color="#5e1ee8" style={{ flexShrink: 0 }} />
</div>


</div>

{/* Floating Help Search Bar */}
<div style={{ marginTop: "2%", marginBottom: 18, padding: "0 16px" }}>
  <LabSearchRow
    onOpenSearch={() => setSearchOpen(true)}
    searchQuery={searchQuery}
    setSearchQuery={setSearchQuery}
  />
</div>

    
      {/* Body */}
      <div style={{ marginTop: "1%",  padding: "0 16px" }}>
        {/* Action Chips */}
  
        {/* End Action Chips */}


{/* Feature Banner Carousel */}
{(() => {
  const featureBanners = [
    {
      bgImage: Banner4,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/symptom-checker",
    },
    {
      bgImage: Banner5,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/epharmacy",
    },
    {
      bgImage: Banner6,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/doctor-consult",
    },
    {
      bgImage: Banner7,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/lab-tests",
    },
    {
      bgImage: Banner8,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/health-wallet",
    },
    {
      bgImage: Banner9,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/emergency-response",
    },
    {
      bgImage: Banner11,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/",
    },
    {
      bgImage: Banner12,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/",
    },
    {
      bgImage: Banner13,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/",
    },
    {
      bgImage: Banner14,
      title: "",
      subtitle: "",
      overlay: "transparent",
      contentStyle: { display: "none" },
      route: "/",
    },
    /*
    {
      gradient: "linear-gradient(118deg, #6f21d4 0%, #8127e3 48%, #5d179e 100%)",
      glowColor: "rgba(216,180,254,0.34)",
      glowLeft: 86, glowTop: -22,
      useImage: false,
      icon: <FlaskConical size={52} strokeWidth={1.6} color="rgba(255,255,255,0.92)" />,
      iconBg: "rgba(255,255,255,0.12)",
      dotColor: "#f5d0fe",
      title: "Lab Results Ready",
      subtitle: "View CBC & full reports",
      route: "/lab-tests",
    },
    {
      gradient: "linear-gradient(118deg, #6f21d4 0%, #8127e3 48%, #5d179e 100%)",
      glowColor: "rgba(196,181,253,0.36)",
      glowLeft: 86, glowTop: -22,
      useImage: false,
      icon: <Pill size={52} strokeWidth={1.6} color="rgba(255,255,255,0.92)" />,
      iconBg: "rgba(255,255,255,0.12)",
      dotColor: "#ddd6fe",
      title: "Pharmacy Open",
      subtitle: "Order meds in minutes",
      route: "/epharmacy",
    },
    {
      gradient: "linear-gradient(118deg, #6f21d4 0%, #8127e3 48%, #5d179e 100%)",
      glowColor: "rgba(233,213,255,0.36)",
      glowLeft: 86, glowTop: -22,
      useImage: false,
      icon: <HeartPulse size={52} strokeWidth={1.6} color="rgba(255,255,255,0.92)" />,
      iconBg: "rgba(255,255,255,0.12)",
      dotColor: "#f0abfc",
      title: "Health Monitor",
      subtitle: "Track chronic conditions",
      route: "/remote",
    },
    {
      gradient: "linear-gradient(118deg, #6f21d4 0%, #8127e3 48%, #5d179e 100%)",
      glowColor: "rgba(167,139,250,0.34)",
      glowLeft: 86, glowTop: -22,
      useImage: false,
      icon: <CreditCard size={52} strokeWidth={1.6} color="rgba(255,255,255,0.92)" />,
      iconBg: "rgba(255,255,255,0.12)",
      dotColor: "#c4b5fd",
      title: "Health Wallet",
      subtitle: "$50 balance available",
      route: "/health-wallet",
    },
    {
      gradient: "linear-gradient(118deg, #6f21d4 0%, #8127e3 48%, #5d179e 100%)",
      glowColor: "rgba(221,214,254,0.36)",
      glowLeft: 86, glowTop: -22,
      useImage: false,
      icon: <Stethoscope size={52} strokeWidth={1.6} color="rgba(255,255,255,0.92)" />,
      iconBg: "rgba(255,255,255,0.12)",
      dotColor: "#ede9fe",
      title: "Symptom Checker",
      subtitle: "Describe how you feel",
      route: "/symptom-checker",
    },*/
  ];

  const handleBannerScroll = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    setActiveBanner(idx);
  };

  const scrollBannerTo = (idx) => {
    if (bannerRef.current) {
      bannerRef.current.scrollTo({ left: idx * bannerRef.current.offsetWidth, behavior: "smooth" });
    }
  };

  return (
    <>
      <div
        ref={bannerRef}
        onScroll={handleBannerScroll}
        className="scrollbar-hide"
        style={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          gap: 0,
          marginBottom: 0,
          borderRadius: 24,
       
          boxShadow: "0 10px 24px rgba(91, 33, 182, 0.22)",
        }}
      >
        {featureBanners.map((b, i) => (
          <div
            key={i}
            onClick={() => navigate(b.route)}
            style={{
              flexShrink: 0,
              width: "100%",
              scrollSnapAlign: "start",
              height: 160,
              position: "relative",
              overflow: "hidden",
              borderRadius: 14,
              cursor: "pointer",
            }}
          >
            {/* Background photo */}
            <img
              src={b.bgImage}
              alt={b.title}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 74%",
              }}
            />
            {/* Overlay */}
            <div style={{ position: "absolute", inset: 0, background: b.overlay }} />

            {b.badge ? (
              <div
                style={{
                  position: "absolute",
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  border: "6px solid #d9d8ff",
                  color: "#ffffff",
                  fontSize: 14,
                  fontWeight: 700,
                  display: "grid",
                  placeItems: "center",
                  zIndex: 2,
                  ...(b.badgeStyle || {}),
                }}
              >
                {b.badge}
              </div>
            ) : null}

            {/* Text */}
            <div
              style={{
                position: "absolute",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                ...(b.contentStyle || {}),
              }}
            >
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
                {b.showDot ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6dfc6d", display: "inline-block" }} /> : null}
                <span style={{ fontSize: 26, fontWeight: 700, color: "#ffffff", whiteSpace: "pre-line", ...(b.titleStyle || {}) }}>{b.title}</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.92)", lineHeight: 1.25, maxWidth: 180, ...(b.subtitleStyle || {}) }}>
                {b.subtitle}
              </div>
            </div>

            {b.cta ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(b.route);
                }}
                style={{
                  position: "absolute",
                  border: "solid 1px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.92)",
                  color: "var(--primary)",
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "5px 11px",
                  zIndex: 2,
                  cursor: "pointer",
                  ...(b.ctaStyle || {}),
                }}
              >
                {b.cta}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {/* Pagination dots */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 8, marginBottom: 10 }}>
        {featureBanners.map((_, i) => (
          <span
            key={i}
            onClick={() => scrollBannerTo(i)}
            style={{
              display: "inline-block",
              width: i === activeBanner ? 22 : 6,
              height: 6,
              borderRadius: 999,
              background: i === activeBanner ? "#d6b8ff" : "#d8c8f5",
              transition: "width 0.25s ease, background 0.25s ease",
              cursor: "pointer",
            }}
          />
        ))}
      </div>
    </>
  );
})()}

<div
  style={{
    marginTop: 12,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }}
>
  <h4 style={{ margin: 0, fontWeight: 700, fontSize: 17, color: "#1f2937" }}> Services</h4>
  <button
    type="button"
    onClick={() => navigate("/all-features")}
    style={{
      border: "none",
      background: "transparent",
      color: "var(--primary)",
      fontWeight: 600,
      fontSize: 16,
      cursor: "pointer",
      
      padding: 0,
    }}
  >
    See All
  </button>
</div>

<QuickActions tiles={tiles} navigateTo={navigateTo}  />






{/* Health Snapshot */}
<HealthSnapshot />

{/* Pharmacy */}
<HomePharmacyPreview />

{/* Nearby Clinics */}
<HomeNearbyClinics />

{/* My Records */}
<div style={{ marginTop: 14, marginBottom: 14 }}>
  <h4 style={{ fontWeight: 700, fontSize: 17, marginBottom: 10, color: "#1f2937" }}>
    My Records
  </h4>

  <div
    onClick={() => navigate("/my-health-records")}
    style={{
      borderRadius: 24,
      border: "1px solid #e2e6ec",
      background: "var(--surface)",
      padding: 0,
      overflow: "hidden",
      display: "grid",
      gridTemplateColumns: "1.35fr 0.65fr",
      cursor: "pointer",
    }}
  >
    <div style={{ padding: 16 }}>
      <p style={{ margin: 0, color: "#4b5563", fontSize: 15, lineHeight: 1.4 }}>
        Let's explore and create your medical record today.
      </p>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate("/my-health-records");
        }}
        style={{
          border: "none",
          background: "transparent",
          color: "var(--primary)",
          fontWeight: 600,
          fontSize: 16,
          padding: 0,
          marginTop: 12,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Explore EHR <i className="fi fi-br-arrow-right" style={{ fontSize: 14 }} />
      </button>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 8,
      }}
    >
      <img
        src={EhrRecord}
        alt="EHR Record"
        style={{ width: 110, height: 110, objectFit: "contain" }}
      />
    </div>
  </div>
</div>

{/* AI Health Assistant */}
<div style={{ marginTop: 6, marginBottom: 16 }}>
  <h4 style={{ fontWeight: 700, fontSize: 17, marginBottom: 10, color: "#1f2937" }}>
    AI Health Assistant
  </h4>

  <div
    style={{
      borderRadius: 24,
      border: "1px solid #e2e6ec",
      background: "var(--surface)",
      padding: 14,
    }}
  >
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#ede9fe",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7c3aed",
          flexShrink: 0,
        }}
      >
        <AutoAwesomeIcon style={{ fontSize: 22 }} />
      </div>

      <div
        style={{
          flex: 1,
          borderRadius: 18,
          border: "1px solid #d9dee7",
          background: "#f2f4f7",
          padding: 12,
        }}
      >
        <p style={{ margin: 0, color: "#374151", fontSize: 15, lineHeight: 1.45 }}>
          {aiAssistantPreview.text}
        </p>

        <div style={{ marginTop: 8, textAlign: "right", fontSize: 12, color: "#6b7280" }}>
          {aiPreviewTimeLabel} <span style={{ color: "var(--primary)", fontWeight: 700 }}>✓✓</span>
        </div>
      </div>
    </div>

    <div style={{ borderTop: "1px solid #dce1e8", marginTop: 12, paddingTop: 12, textAlign: "center" }}>
      <button
        onClick={() => navigate("/asha-cover")}
        style={{
          border: "none",
          background: "transparent",
          color: "var(--primary)",
          fontWeight: 600,
          fontSize: 16,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: 0,
        }}
      >
        See In Detail <i className="fi fi-br-arrow-right" style={{ fontSize: 14 }} />
      </button>
    </div>
  </div>
</div>


 {/* SYMPTOM CHECKER */}
   <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>
          Symptom Checker
        </h3>
<div style={{  marginBottom: 16 }}>
  <div
    style={{
      background: "var(--surface)",
      borderRadius: 18,
      padding: 16,
      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
      border: "1px solid #edf0f5",
    }}
  >
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
      <div>
      
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
          <span style={{ fontSize: 40, lineHeight: 1, fontWeight: 700, color: "#1f2937" }}>
            {symptomSummary.totalConditions || 0}
          </span>
          <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.3 }}>
            <div>Possible Conditions</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>{symptomSummaryTimeLabel}</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => navigate("/symptom-checker")}
        aria-label="Open symptom checker"
        style={{
          width: 46,
          height: 46,
          borderRadius: "50%",
          border: "1px solid #d7dde6",
          background: "#f8fafc",
          display: "grid",
          placeItems: "center",
          color: "#334155",
          flexShrink: 0,
        }}
      >
        <Plus size={22} />
      </button>
    </div>

    {symptomSummary.items.length > 0 ? (
      symptomSummary.items.map((item) => (
        <button
          key={item.name}
          onClick={() => navigate("/symptom-checker")}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            padding: "10px 0",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            textAlign: "left",
            borderBottom: "1px solid #f1f4f8",
          }}
        >
          <div style={{ display: "flex", gap: 12, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: item.bg,
                color: item.accent,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Brain size={18} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name}
              </div>

              <div style={{ marginTop: 8, height: 7, borderRadius: 999, background: "#edf1f5", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${item.progress}%`,
                    height: "100%",
                    borderRadius: 999,
                    background: item.accent,
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#475569", fontSize: 14, fontWeight: 600 }}>
                  <HeartPulse size={15} color="#94a3b8" />
                  {item.summary}
                </span>

                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: symptomSummary.risk.color, fontSize: 14, fontWeight: 600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: symptomSummary.risk.color, display: "inline-block" }} />
                  {symptomSummary.risk.label}
                </span>
              </div>

              <div style={{ marginTop: 8, color: "#6b7280", fontSize: 14 }}>
                {symptomSummary.careStepCount || 0} care steps • {symptomSummary.risk.followUp}
              </div>
            </div>
          </div>

          <ChevronRight size={20} color="#64748b" style={{ flexShrink: 0, marginTop: 4 }} />
        </button>
      ))
    ) : (
      <button
        onClick={() => navigate("/symptom-checker")}
        style={{
          width: "100%",
          border: "1px dashed #d7dde6",
          borderRadius: 16,
          background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
          padding: 16,
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#eef6ff", color: "#0f766e", display: "grid", placeItems: "center" }}>
            <ScanHeart size={18} />
          </div>
          <strong style={{ color: "#1f2937" }}>Start a symptom review</strong>
        </div>
        <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.5 }}>
          Run a quick symptom check to see possible causes, urgency, and recommended next steps.
        </div>
      </button>
    )}

    <div style={{ borderTop: "1px solid #dce1e8", marginTop: 14, paddingTop: 12, textAlign: "center" }}>
      <button
        onClick={() => navigate("/symptom-checker")}
        style={{
          border: "none",
          background: "transparent",
          color: "var(--primary)",
          fontWeight: 600,
          fontSize: 16,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: 0,
        }}
      >
        See All
      </button>
    </div>
  </div>
</div>
<br/>

{/* Upcoming appointments */}

<Upcoming_appointments />




        {/* Health Education */}
        <div style={{ marginTop: 30 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h4 style={{ fontWeight: 700, margin: 0, fontSize: 17, color: "#1f2937" }}>News & Resources</h4>
            <button
              onClick={() => navigate("/blogs")}
              style={{ border: "none", background: "transparent", color: "var(--primary)", fontWeight: 600, fontSize: 16, padding: 0 }}
            >
              See All
            </button>
          </div>

          <div
            ref={carouselRef}
            {...handlers}
            className="scrollbar-hide"
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              paddingBottom: 4,
            }}
          >
            {blogsLoading &&
              [1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    minWidth: 314,
                    borderRadius: 24,
                    border: "1px solid #e2e6ec",
                    background: "var(--surface)",
                    padding: 12,
                    height: 330,
                  }}
                />
              ))}

            {blogsError && <div style={{ marginTop: 8, color: "var(--muted)" }}>{blogsError}</div>}

              {!blogsLoading && blogs.map((b, index) => {
              const articleId = b._id || b.id || index;
              const articleDate = b.createdAt || b.publishedAt || b.date;
              const formattedDate = articleDate
                ? new Date(articleDate).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
                : "Jun 23, 2025";
              const authorName = b.author || "Julie Robertson";
              const authorInitial = authorName?.charAt(0)?.toUpperCase() || "J";

              return (
                <motion.div
                  key={articleId}
                  onClick={() => navigate(`/blog/${articleId}`)}
                  style={{
                    minWidth: 314,
                    borderRadius: 24,
                    border: "1px solid #e2e6ec",
                    background: "var(--surface)",
                    padding: 12,
                    scrollSnapAlign: "start",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={b.mediaUrl || b.thumbnail || mock_thumbnail}
                    alt={b.title}
                    style={{ width: "100%", height: 182, objectFit: "cover", borderRadius: 18, marginBottom: 10 }}
                  />

                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontSize: 14, marginBottom: 8 }}>
                    <span>{formattedDate}</span>
                    <span style={{ color: "#c4c9d3" }}>•</span>
                    <span>{b.category || "Wellness"}</span>
                  </div>

                  <div
                    style={{
                      fontSize: 16,
                      lineHeight: 1.22,
                      fontWeight: 600,
                      color: "#1f2937",
                      minHeight: 56,
                      marginBottom: 10,
                    }}
                  >
                    {(b.title || "Learn about cardio fitness & how it's measured").slice(0, 64)}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #cbd5e1, #94a3b8)",
                          color: "#111827",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {authorInitial}
                      </div>
                      <span style={{ fontSize: 15, color: "#4b5563" }}>{authorName}</span>
                    </div>

                    <i className="fi fi-br-arrow-up-right" style={{ color: "#98a2b3", fontSize: 18 }} />
                  </div>
                </motion.div>
              );
            })}

            {!blogsLoading && !blogs.length && !blogsError && (
              <div style={{ marginTop: 8, color: "var(--muted)" }}>No articles available yet.</div>
            )}
          </div>

        </div>
<br></br>
            {/* 
      Support
      */}

            <div className="w-full max-w-md space-y-4">
        {/* Rate Card */}
        <div
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #fbfbfc 100%)",
            border: "1px solid #edf0f3",
            borderRadius: 24,
            padding: "18px 18px 20px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "#1f2937",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.02em",
            }}
          >
            Rate Our App
          </h2>
          <p
            style={{
              margin: "8px auto 18px",
              maxWidth: 270,
              color: "#98a2b3",
              fontSize: 12,
              lineHeight: 1.45,
              fontWeight: 500,
            }}
          >
            Help us improve our platform by giving us honest feedback.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
              {ratingImages.map((rating, index) => {
              const isActive = selected === index;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelected(index)}
                    aria-label={`Rate ${rating.label}`}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: isActive ? "1px solid #f4b740" : "1px solid #eef1f4",
                    background: isActive ? "#ffe7a3" : "#f3f4f6",
                    color: isActive ? "#9a6700" : "#c4cad3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    lineHeight: 1,
                    cursor: "pointer",
                    boxShadow: isActive ? "0 8px 16px rgba(244, 183, 64, 0.22)" : "none",
                    transform: isActive ? "translateY(-1px)" : "none",
                    transition: "all 160ms ease",
                    filter: isActive ? "none" : "grayscale(0.2)",
                  }}
                >
                    <img
                      src={rating.src}
                      alt={rating.label}
                      style={{
                        width: 24,
                        height: 24,
                        opacity: isActive ? 1 : 0.58,
                        objectFit: "contain",
                      }}
                    />
                </button>
              );
            })}
          </div>
        </div>
        <br></br>
  {/* Support Card */}
        <div
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: "1px solid #e8edf3",
            borderRadius: 26,
            padding: "16px 16px 16px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            boxShadow: "0 18px 36px rgba(15, 23, 42, 0.07)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(90deg, rgba(127, 0, 255, 0.02) 0%, rgba(127, 0, 255, 0) 56%)",
              pointerEvents: "none",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                color: "#667085",
                fontSize: 12,
                lineHeight: 1.55,
                fontWeight: 500,
                maxWidth: 180,
              }}
            >
              Need any help? We&apos;re always here to help.
            </p>
            <button
              type="button"
              onClick={() => navigate('/support')}
              style={{
                marginTop: 10,
                padding: 0,
                border: "none",
                background: "transparent",
                color: "var(--primary)",
                fontSize: 12,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
              }}
            >
              Contact Support
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <div
            style={{
              position: "relative",
              width: 96,
              height: 96,
              borderRadius: 28,
              background: "linear-gradient(145deg, rgba(127, 0, 255, 0.12) 0%, rgba(127, 0, 255, 0.04) 100%)",
              border: "1px solid rgba(127, 0, 255, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "conic-gradient(from 210deg, rgba(127, 0, 255, 0.2) 0deg, var(--primary) 155deg, #a855f7 255deg, rgba(127, 0, 255, 0.14) 360deg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 22px rgba(127, 0, 255, 0.18)",
                  border: "1px solid rgba(127, 0, 255, 0.08)",
                }}
              >
                <HeadsetMicIcon style={{ fontSize: 25, color: "var(--primary)" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

  


      {/* Bottom Nav */}
      <BottomNavigation isListening={isListening} transcript={transcript} />
{isListening && (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
    className="voice-overlay"
  >
    <div className="voice-box card-theme">
      <img
        src={Logo}
        alt="Listening"

        style={{

          width : 70,
          height : 70
        }}
        className="voice-icon"
      />
      <p className="voice-text">{transcript || "Listening..."}</p>
    </div>
  </motion.div>
)}
</div>
  );
}

export default Home;